// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ProjectModel } from '@project/model/project.model';
import { ArticleMoveManager } from '@project/components/article/manager/article-move.manager';
import { SceneManager } from '../manager/scene.manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { CompositeArticleManager } from '@article/manager/composite-article.manager';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { ArticlesVisibilityService } from '@project/components/article/service/articles-visibility.service';
import { BridgeArticleManager } from '@project/components/article/manager/bridge-article.manager';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { LabelStripArticleManager } from '../manager/label-strip-article.manager';
import { ImageTextureProcessor } from '../processor/image-texture.processor';
import { ArticleContainerFactory } from './article-container.factory';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';
import { ProjectVO } from '@project/model/vo/project.vo';
import { CompositionContainer } from './composition-container';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleCompositionVO } from '@article/model/vo/article.vo';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { ProjectChanged, ProjectChangedType } from '@project/model/project-changed';
import { GlobalContainer } from './global-container';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { AttachCodingFingerArticleService } from '@project/components/article/service/attach-coding-finger-article.service';
import { DetachCodingFingerArticleService } from '@project/components/article/service/detach-coding-finger-article.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ContainerUtils } from './container.utils';

@Injectable({ providedIn: 'root' })
export class GlobalContainerService {
  constructor (
    private globalContainer: GlobalContainer,
    private projectModel: ProjectModel,
    private articleMoveManager: ArticleMoveManager,
    private sceneManager: SceneManager,
    private projectContentPresenter: ProjectContentPresenter,
    private compositeArticleManager: CompositeArticleManager,
    private articleLoadingManager: ArticleLoadingManager,
    private articlesVisibilityService: ArticlesVisibilityService,
    private bridgeArticleManager: BridgeArticleManager,
    private carrierBlockManager: CarrierBlockManager,
    private attachCodingFingerArticleService: AttachCodingFingerArticleService,
    private detachCodingFingerArticleService: DetachCodingFingerArticleService,
    private labelStripArticleManager: LabelStripArticleManager,
    private imageTextureProcessor: ImageTextureProcessor,
    private articleContainerFactory: ArticleContainerFactory,
    private _articleContainerAttachedService: ArticleContainerAttachedService,
    private _attachArticleUsageService: AttachArticleUsageService
  ) {
    this.addMessageHandlers();
  }

  private addMessageHandlers(): void {
    this.projectModel.projectChanged$.subscribe(projectChanged => this.projectChanged(projectChanged));
  }

  public updateProjectModel(project: ProjectVO): void {
    this.recreateCompositions(project.compositions);

    // refresh compositions
    for (const child of this.globalContainer.children) {
      if (!ContainerUtils.isCompositionContainer(child)) {
        continue;
      }

      const compositionContainer: CompositionContainer = child as CompositionContainer;
      // change position
      const xposition: number = PropertyUtils.getFloatPropertyValue(
        compositionContainer.composition.infoProperties,
        ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X
      );
      const yposition: number = PropertyUtils.getFloatPropertyValue(
        compositionContainer.composition.infoProperties,
        ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y
      );
      const zposition: number = PropertyUtils.getFloatPropertyValue(
        compositionContainer.composition.infoProperties,
        ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z
      );
      // apply left-handed position specified in flex-version (move in -x direction)
      compositionContainer.moveTo(-xposition, yposition, zposition);

      // move articles
      compositionContainer.updateProjectModel();
    }
  }

  private refreshContainer(articleUsageIdsToRefresh: string[] = null): void {
    this.recreateCompositions(this.projectModel.currentProject.compositions);
    // refresh compositions
    for (const child of this.globalContainer.children) {
      if (ContainerUtils.isCompositionContainer(child)) {
        const compositionContainer: CompositionContainer = child as CompositionContainer;
        compositionContainer.refreshContainer(articleUsageIdsToRefresh);
      }
    }
  }

  private recreateCompositions(compositions: ArticleCompositionVO[]): void {
    compositions = compositions.concat();
    for (const child of this.globalContainer.children) {
      if (ContainerUtils.isCompositionContainer(child)) {
        const compositionContainer: CompositionContainer = child as CompositionContainer;
        if(!ArrayUtils.removeItem(compositionContainer.composition, compositions)) {
          this.globalContainer.remove(compositionContainer);
          Object3DUtils.dispose(compositionContainer);
        }
      }
    }

    // add new compositions
    for (const articleComposition of compositions) {
      const compositionContainer: CompositionContainer = new CompositionContainer(
        this.projectModel,
        this.articleMoveManager,
        this.sceneManager,
        this.projectContentPresenter,
        this.compositeArticleManager,
        this.articleLoadingManager,
        this.articlesVisibilityService,
        this.bridgeArticleManager,
        this.carrierBlockManager,
        this.attachCodingFingerArticleService,
        this.detachCodingFingerArticleService,
        this.labelStripArticleManager,
        this.imageTextureProcessor,
        this.articleContainerFactory,
        this._articleContainerAttachedService,
        this._attachArticleUsageService
      );
      this.globalContainer.add(compositionContainer);
      compositionContainer.composition = articleComposition;
    }
  }

  private projectChanged(projectChanged: ProjectChanged) {
    switch (projectChanged.type) {
      case ProjectChangedType.ProjectChange:
        this.refreshContainer(projectChanged.articleUsageIdsToRefresh);
        break;
    }
  }
}
